import { Button, Classes, Dialog, FormGroup, IDialogProps, InputGroup, Intent, TagInput } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import { UprightStyle } from '../../../../../types'
import { ProductOption as APIProductOption, ProductVariant } from '../../../../../types/api'
import { pluralizeFromNumber } from '../../../../../utils/string'
import { MAX_PRODUCT_OPTIONS, MAX_PRODUCT_VARIANTS } from '../constants'
import { countVariations } from '../utils'
import { FormChangeHandler } from './inputs'
import DeleteDialog from './Options/DeleteDialog'

interface ProductOption extends APIProductOption {
  nameValidations?: string[]
  valuesValidations?: string[]
}

interface Props {
  dialogProps: IDialogProps
  productOptions: ProductOption[]
  onChange: FormChangeHandler
  productOrderCount: number
  variants: ProductVariant[]
}

const styles: UprightStyle = {
  dialog: {
    width: 440
  },
  noBottomMargin: {
    marginBottom: 6
  },
  callout: { marginBottom: 20 },
  optionSection: { marginBottom: 20 },
  totalVariants: { margin: `0 auto 0 0 ` },
  cancelSpacing: { marginRight: 10 },
  dialogHeader: { marginBottom: 25 },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20
  },
  noMargin: { margin: 0 },
  alignCenter: { alignItems: 'center' },
  addOptionButton: { marginTop: -10 }
}

function nicePlaceholder(len: number) {
  if (len === 2) return { name: 'Color', values: 'Red, Blue, Green' }
  if (len === 3) return { name: 'Material', values: 'Leather, Fleece, Denim' }

  return { name: 'Size', values: 'Small, Medium, Large' }
}

const NEW_OPTION: ProductOption = { name: '', values: [], nameValidations: [], valuesValidations: [] }
const NAME_TAKEN_ERROR = 'Name is already taken '

const OptionModal = (props: Props) => {
  const [options, setOptions] = React.useState<ProductOption[]>(props.productOptions)
  const [deletedOptions, setDeletedOptions] = React.useState<ProductOption[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState<number>(-1)

  React.useEffect(() => {
    if (props.productOptions.length === 0) setOptions([NEW_OPTION])
  }, [props.productOptions.length])

  const placeholders = nicePlaceholder(options.length)
  const totalVariants = countVariations(options)

  const productHasOrders = props.productOrderCount > 0
  const maxOptionValuesExceeded = options.length >= MAX_PRODUCT_OPTIONS
  const addOptionButtonDisabled = productHasOrders || maxOptionValuesExceeded
  let addOptionButtonTitle
  if (addOptionButtonDisabled) {
    addOptionButtonTitle = maxOptionValuesExceeded
      ? `Limit of ${MAX_PRODUCT_OPTIONS} options reached`
      : `You cannot add options to products with orders`
  }

  const addOptionValueValidationMessages = optionsData => {
    let newOptions = optionsData.map(o => {
      if (o.values.some(v => v.includes('/'))) return { ...o, valuesValidations: ['Values cannot contain "/" '] }
      return { ...o, valuesValidations: [] }
    })

    const tooManyVariants = countVariations(newOptions) > MAX_PRODUCT_VARIANTS

    if (tooManyVariants) {
      newOptions = newOptions.map(o => {
        return {
          ...o,
          valuesValidations: [`${MAX_PRODUCT_VARIANTS} variant limit reached – remove some values to save`]
        }
      })
    }
    return newOptions
  }

  const setOptionsAndValidate = optionsData => {
    setOptions(addOptionValueValidationMessages(optionsData))
  }

  function onAddOption() {
    setOptionsAndValidate([...options, NEW_OPTION])
  }

  function onRemoveOption(idx: number) {
    // If the product has existing variants, warn the user that the variants table will be rewritten
    if (props.variants?.length > 0 && deleteDialogOpen === -1) {
      setDeleteDialogOpen(idx)
      return
    }

    // If the option has an Id AKA has been saved to the DB, mark it as destroyable
    if (options[idx].id) {
      setDeletedOptions([...deletedOptions, options[idx]])
    }

    let newOptions = [...options.filter((_, i) => i !== idx)]
    newOptions = newOptions.map(newOpt => {
      const o = { ...newOpt }
      if (newOptions.length > 1 && newOptions.findIndex(n => n.name === o.name) > -1) {
        o.nameValidations = [NAME_TAKEN_ERROR]
      } else if (o.nameValidations.includes(NAME_TAKEN_ERROR)) {
        o.nameValidations = []
      }

      return o
    })

    setOptionsAndValidate(newOptions)
    if (deleteDialogOpen !== -1) {
      setDeleteDialogOpen(-1)
    }
  }

  function onOptionChange(idx: number, key: keyof ProductOption, value) {
    const newOptions = options.map((o, i) => {
      if (i !== idx) return o
      return { ...o, [key]: value }
    })
    setOptionsAndValidate(newOptions)
  }

  function onSave(e) {
    const deletedOpts = deletedOptions.map(o => o.id)

    if (deletedOpts.length > 0) {
      props.onChange({ name: 'optionIdsToDestroy', value: deletedOpts })
    }

    props.onChange({ name: 'options', value: options.map((o, i) => ({ ...o, position: i })) })
    props.dialogProps.onClose(e)
  }

  function validateNames(idx: number) {
    setOptions(
      options.map((o, i) => {
        if (o.name.includes('/')) {
          return { ...o, nameValidations: ['Name cannot contain "/" '] }
        }

        const duplicates = options.filter(opt => opt.name === o.name)
        if (i === idx && duplicates.length > 1) {
          return { ...o, nameValidations: [NAME_TAKEN_ERROR] }
        }

        return { ...o, nameValidations: [] }
      })
    )
  }

  /**
   * All options names present
   * All options have values
   * No option name or values validation issues
   */
  function validateSave() {
    return options.every(o => {
      return (
        o.name.trim() &&
        o.values.length > 0 &&
        (o.nameValidations || []).length === 0 &&
        (o.valuesValidations || []).length === 0
      )
    })
  }

  return (
    <Dialog
      style={styles.dialog}
      {...props.dialogProps}
      title="Options"
      isCloseButtonShown={false}
      canOutsideClickClose={false}
      canEscapeKeyClose={false}
      portalClassName="upright-popover-safe-dialog"
    >
      <div className={Classes.DIALOG_BODY}>
        <p style={styles.dialogHeader}>
          Add up to three options to express differences across the product (size, color, material etc.)
        </p>
        {productHasOrders && (
          <p>
            Since this product has orders you won&apos;t be able remove options, try setting the quantity to 0 instead
          </p>
        )}

        <DeleteDialog
          isOpen={deleteDialogOpen >= 0}
          onCancel={() => setDeleteDialogOpen(-1)}
          onClose={() => onRemoveOption(deleteDialogOpen)}
        />

        {/* Options may or may not have a unique ID, the index is good enough */}
        {/* eslint-disable react/no-array-index-key */}
        {options.map((po, idx) => {
          const deleteOptionButtonDisabled =
            productHasOrders || (options.length === 1 && idx === 0 && props.productOptions.length === 0)

          return (
            <div key={idx} style={styles.optionSection}>
              <div style={styles.rowContainer}>
                <h5 className="section-header" style={styles.noMargin}>
                  Option {idx + 1}
                </h5>
                <Button
                  disabled={deleteOptionButtonDisabled}
                  title={productHasOrders ? 'You cannot remove options from a product with orders' : undefined}
                  icon={IconNames.TRASH}
                  tabIndex={-1}
                  onClick={() => onRemoveOption(idx)}
                  minimal
                />
              </div>
              <div>
                <FormGroup
                  label="Name"
                  helperText={po.nameValidations?.join(', ')}
                  intent={po.nameValidations?.length > 0 ? Intent.DANGER : Intent.NONE}
                >
                  <InputGroup
                    autoFocus
                    onBlur={() => validateNames(idx)}
                    placeholder={placeholders.name}
                    onChange={e => onOptionChange(idx, 'name', e.target.value)}
                    value={po.name}
                  />
                </FormGroup>
                <FormGroup
                  label="Values"
                  intent={po.valuesValidations?.length > 0 ? Intent.DANGER : Intent.NONE}
                  helperText={po.valuesValidations?.join(', ') || 'Use commas to separate values'}
                >
                  <TagInput
                    placeholder={placeholders.values}
                    separator=","
                    onAdd={addedValues => {
                      onOptionChange(idx, 'values', Array.from(new Set([...po.values, ...addedValues])))
                    }}
                    onRemove={removedValue => {
                      // This function also supports backspace removal functionality, so it's necessary to specify
                      // even when tagProps.onRemove is present
                      if (productHasOrders) return // remove is disabled when a product has orders
                      onOptionChange(
                        idx,
                        'values',
                        po.values.filter(v => v !== removedValue)
                      )
                    }}
                    addOnBlur
                    tagProps={{
                      onRemove: productHasOrders
                        ? undefined // remove is disabled when a product has orders
                        : (event: React.MouseEvent<HTMLSpanElement>) => {
                            const index = +event.currentTarget.parentElement.getAttribute('data-tag-index')
                            onOptionChange(
                              idx,
                              'values',
                              po.values.filter((v, i) => i !== index)
                            )
                          }
                    }}
                    values={po.values}
                  />
                </FormGroup>
              </div>

              <div className="hairline-divider" />
            </div>
          )
        })}

        <Button
          style={styles.addOptionButton}
          onClick={onAddOption}
          minimal
          className="reset-button-spacing"
          icon={IconNames.ADD}
          intent={Intent.NONE}
          disabled={addOptionButtonDisabled}
          title={addOptionButtonTitle}
        >
          Add option
        </Button>
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS} style={styles.alignCenter}>
          {totalVariants > 0 && (
            <p style={styles.totalVariants}>
              {totalVariants} total variant{pluralizeFromNumber(totalVariants)}
            </p>
          )}

          <Button style={styles.cancelSpacing} onClick={props.dialogProps.onClose}>
            Cancel
          </Button>
          <Button disabled={!validateSave()} onClick={e => onSave(e)} intent={Intent.PRIMARY}>
            Save Options
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
export default OptionModal
